import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useJotFormData } from "@/hooks/useJotFormData";
import { DashboardLayout } from "@/components/jotform/DashboardLayout";
import {
  FileText,
  AlertTriangle,
  RefreshCw
} from "lucide-react";

const JotFormDashboard = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  
  const {
    forms,
    isLoadingForms,
    selectedFormId,
    setSelectedFormId,
    selectedForm,
    submissions,
    isLoadingSubmissions,
    analysisData,
    calculateAverage,
    error
  } = useJotFormData();

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  if (isLoadingForms) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-80" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <div className="grid gap-4 md:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight">Informes</h1>
        </div>
        <Card className="border-destructive/50 bg-destructive/5">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-destructive mb-2">Error al cargar datos</h3>
            <p className="text-muted-foreground mb-6">{error.message || 'Ha ocurrido un error inesperado'}</p>
            <Button onClick={handleRefresh} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!selectedFormId || !selectedForm) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Informes</h1>
          <p className="text-muted-foreground">
            Selecciona un formulario para ver el análisis de datos
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">Seleccionar Formulario</h3>
            </div>
            <Select value={selectedFormId || ""} onValueChange={setSelectedFormId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecciona un formulario para analizar" />
              </SelectTrigger>
              <SelectContent>
                {forms?.map((form: any) => (
                  <SelectItem key={form.id} value={form.id}>
                    <div className="flex items-center justify-between w-full">
                      <span className="truncate">{form.title}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        ({form.submission_count} respuestas)
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div className="space-y-1">
          <h1 className="text-4xl font-bold tracking-tight">Informes</h1>
          <p className="text-muted-foreground">
            Análisis del formulario: {selectedForm.title}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedFormId || ""} onValueChange={setSelectedFormId}>
            <SelectTrigger className="w-80">
              <SelectValue placeholder="Seleccionar formulario para analizar" />
            </SelectTrigger>
            <SelectContent>
              {forms?.map((form: any) => (
                <SelectItem key={form.id} value={form.id}>
                  <div className="flex items-center justify-between w-full">
                    <span className="truncate">{form.title}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({form.submission_count} respuestas)
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <DashboardLayout
        selectedForm={selectedForm}
        analysisData={analysisData}
        isLoadingSubmissions={isLoadingSubmissions}
        onRefresh={handleRefresh}
        calculateAverage={calculateAverage}
      />
    </div>
  );
};

export default JotFormDashboard;